import React, { ComponentType, isValidElement, ReactNode, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { usePopper } from 'react-popper'
import { Transition } from 'react-transition-group'

import { useIsMounted } from '../helper/hooks/use-is-mounted.js'
import { IconProps, InfoIcon } from '../icon/icon.js'

export type TooltipTriggerEvent = 'click' | 'hover'

export interface TooltipProps {
    /**
     * Content.
     */
    children: string | ReactNode
    /**
     * Id of element that provides the tooltip title.
     */
    'aria-labelledby'?: string
    /**
     * Accepts a string of custom classes to be added to .tooltip
     */
    className?: string
    /**
     * Change tooltip color. Default is black.
     */
    color?: 'white'
    /**
     * Should the tooltip be initially shown.
     */
    defaultIsShown?: boolean
    /**
     * Custom HTML element
     */
    element?: 'button' | 'span'
    /**
     * Tooltip element reference
     */
    elementRef?: (element: HTMLElement | null) => void
    /**
     * Use to handle state outside of component.
     */
    isShown?: boolean
    /**
     * Callback that is invoked when tooltip opens
     */
    onShow?: () => void
    /**
     * Callback that is invoked when tooltip closes
     */
    onHide?: () => void
    /**
     * Callback that is invoked when tooltip visibility changes
     */
    onToggle?: (isShown: boolean) => void
    /**
     * Tooltip trigger element. Defaults is info icon.
     */
    trigger?: ComponentType<IconProps> | string | ReactNode
    /**
     * Event that triggers tooltip.
     */
    triggerEvent?: TooltipTriggerEvent
}

export const Tooltip = (props: TooltipProps): JSX.Element => {
    const tooltip = useRef<HTMLElement | null>(null)
    const [isShown, setIsShown] = useState<boolean>(props.defaultIsShown || false)
    const [isHoveringContent, setIsHoveringContent] = useState<boolean>(false)
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
    const isMounted = useIsMounted()
    const { attributes, styles } = usePopper(referenceElement, popperElement, {
        placement: 'top',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [-6, 0],
                },
            },
        ],
    })

    const getIsShown = (): boolean => {
        return (props.onToggle || (props.onHide && props.onShow)) && typeof props.isShown !== 'undefined'
            ? props.isShown
            : isShown
    }

    const onClickOutside = (event: MouseEvent): void => {
        if (event.target instanceof Element) {
            hideIfOutside(event.target)
        }
    }

    const hideIfOutside = (target: Element | null): void => {
        if (getIsShown()) {
            while (target !== null) {
                if (target === tooltip.current || target === referenceElement) {
                    return
                }

                target = target.parentElement
            }

            hideTooltip()
        }
    }

    const { children, element = 'button', triggerEvent = 'hover' } = props

    const onMouseEnter = (): void => {
        if (!isShown) {
            showTooltip()
        }
    }

    const onMouseLeave = (): void => {
        if (isShown) {
            hideTooltip()
        }
    }

    const onClick = (): void => {
        toggle()
    }

    const showTooltip = (): void => {
        setIsShown(true)

        if (props.onShow) {
            props.onShow()
        }

        if (props.onToggle) {
            props.onToggle(true)
        }
    }

    const hideTooltip = (): void => {
        setIsShown(false)

        if (props.onHide) {
            props.onHide()
        }

        if (props.onToggle) {
            props.onToggle(false)
        }
    }

    const toggle = (): void => {
        if (!isShown) {
            showTooltip()
        } else {
            hideTooltip()
        }
    }

    const onEntered = (): void => {
        if (triggerEvent === 'click') {
            document.addEventListener('click', onClickOutside)
        }
    }

    const onExited = (): void => {
        if (triggerEvent === 'click') {
            document.removeEventListener('click', onClickOutside)
        }
    }

    const BEM = (state?: string): string => {
        const classArray = ['tooltip']

        if (state) {
            classArray.push(`is-${state}`)
        }

        if (getIsShown() || isHoveringContent) {
            classArray.push('is-shown')
        }

        if (props.color) {
            classArray.push(`tooltip--color-${props.color}`)
        }

        if (props.className) {
            classArray.push(props.className)
        }

        return classArray.join(' ')
    }

    const setRef = (el: HTMLElement | null): void => {
        if (el && tooltip.current !== el) {
            tooltip.current = el
        }

        setPopperElement(el)

        if (props.elementRef) {
            props.elementRef(el)
        }
    }

    const onMouseEnterContent = (): void => {
        setIsHoveringContent(true)
    }

    const onMouseLeaveContent = (): void => {
        setIsHoveringContent(false)
    }

    const renderTooltip = (): JSX.Element => {
        const content = (state?: string) => (
            <div
                {...attributes.popper}
                className={BEM(state)}
                style={styles.popper}
                ref={setRef}
                tabIndex={-1}
                role="dialog"
                aria-modal={true}
                aria-labelledby={props['aria-labelledby']}
                onMouseEnter={onMouseEnterContent}
                onMouseLeave={onMouseLeaveContent}
            >
                <div className="tooltip__content">{children}</div>
            </div>
        )

        return (
            <Transition
                in={isHoveringContent || getIsShown()}
                timeout={{ enter: 0, exit: 100 }}
                duration={150}
                mountOnEnter={true}
                unmountOnExit={true}
                onEntered={onEntered}
                onExited={onExited}
            >
                {content}
            </Transition>
        )
    }

    const renderTriggerIcon = (): JSX.Element | string => {
        if (typeof props.trigger === 'string' || isValidElement(props.trigger)) {
            return props.trigger
        }

        return <InfoIcon className="tooltip__icon" />
    }

    const InnerElement = element

    return (
        <>
            <InnerElement
                className="tooltip-toggle"
                ref={setReferenceElement}
                onMouseEnter={triggerEvent === 'hover' ? onMouseEnter : undefined}
                onMouseLeave={triggerEvent === 'hover' ? onMouseLeave : undefined}
                onClick={triggerEvent === 'click' ? onClick : undefined}
                type="button"
            >
                {renderTriggerIcon()}
            </InnerElement>
            {isMounted ? ReactDOM.createPortal(renderTooltip(), document.body) : renderTooltip()}
        </>
    )
}
